import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdArrowBack, MdSave, MdClose } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import { allChannels } from 'variables/industries';
import { fetchIndustries, Industry } from 'services/industryService';
import { userDisplayName } from 'utils/userName';

interface UserInfo {
    _id: string;
    name?: string;
    surname?: string;
    email: string;
    company?: string;
}

interface ReferalInfo {
    code: string;
    type: 'affiliate' | 'coupon' | 'affiliate+coupon' | 'referral';
    costPerLead: number;
    couponFixedValue: number;
    affiliateCommission: number;
}

interface OfferDetail {
    _id: string;
    offerType: 'marketplace' | 'direct';
    industry?: string;
    subIndustry?: string;
    status?: string;
    sellerId?: UserInfo | null;
    buyerId?: UserInfo | null;
    sellerPrice?: number;
    buyerPrice?: number;
    targetPrice?: string;
    leadsGenerated?: number;
    weeklyQuantity?: string;
    offerNumber?: string;
    channels?: string[];
    mandatoryChannels?: string[];
    regions?: string[];
    acceptSideSeller?: boolean;
    createdAt: string;
    updatedAt?: string;
    sheetUrl?: string | null;
}

const REJECTION_REASONS = [
    'Numéro de téléphone invalide',
    'Numéro injoignable',
    'Coordonnées incomplètes',
    'Informations incohérentes',
    "Le prospect n'a jamais effectué cette demande",
    'Prospect non intéressé',
    'Demande réalisée par erreur',
    'Type de projet incorrect',
    'Travaux déjà réalisés',
    'Projet trop éloigné dans le temps',
    'Aucune intention de réaliser les travaux',
    'Doublon détecté',
    'Coordonnées suspectes ou fictives',
    'Comportement frauduleux détecté',
    'Consentement RGPD absent',
];

interface Lead {
    _id: string;
    sellerId: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    buyerId: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    orderNumber: string;
    status: string;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
    leadrsRate: number;
    customerRate: number;
    comment?: string;
    leadPrice?: number;
    buyerPrice?: number;
    email?: string;
    createdAt: string;
}

export default function BackOfficeOfferDetail() {
    const { offerType, id } = useParams<{ offerType: string; id: string }>();
    const navigate = useNavigate();
    const [offer, setOffer] = useState<OfferDetail | null>(null);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLeads, setTotalLeads] = useState(0);
    const [sellerReferalInfo, setSellerReferalInfo] = useState<ReferalInfo | null>(null);
    const [buyerReferalInfo, setBuyerReferalInfo] = useState<ReferalInfo | null>(null);
    const [editSellerPrice, setEditSellerPrice] = useState<string>('');
    const [editBuyerPrice, setEditBuyerPrice] = useState<string>('');
    const [savingPrices, setSavingPrices] = useState(false);
    const [priceSaveError, setPriceSaveError] = useState<string | null>(null);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [savingSubIndustry, setSavingSubIndustry] = useState(false);
    const [rejectingLeadId, setRejectingLeadId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        fetchOfferDetail();
        fetchIndustries().then(setIndustries).catch((error) => console.error('Error fetching industries:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, offerType, page]);

    const fetchOfferDetail = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page, limit: 50 },
            });

            setOffer(response.data.offer);
            setLeads(response.data.leads);
            setTotalLeads(response.data.totalLeads);
            setTotalPages(response.data.pages);
            setSellerReferalInfo(response.data.sellerReferalInfo ?? null);
            setBuyerReferalInfo(response.data.buyerReferalInfo ?? null);
            setEditSellerPrice(response.data.offer.sellerPrice != null ? String(response.data.offer.sellerPrice) : '');
            setEditBuyerPrice(response.data.offer.buyerPrice != null ? String(response.data.offer.buyerPrice) : '');
        } catch (error) {
            console.error('Error fetching offer detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAcceptSideSeller = async () => {
        if (!offer) return;
        try {
            const token = localStorage.getItem('token');
            const newValue = !offer.acceptSideSeller;
            await axios.patch(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { acceptSideSeller: newValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOffer({ ...offer, acceptSideSeller: newValue });
        } catch (error) {
            console.error('Error updating acceptSideSeller:', error);
            alert('Failed to update');
        }
    };

    const handleSubIndustryChange = async (newSubIndustry: string) => {
        if (!offer || newSubIndustry === (offer.subIndustry || '')) return;
        setSavingSubIndustry(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { subIndustry: newSubIndustry },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOffer({ ...offer, subIndustry: newSubIndustry });
        } catch (error) {
            console.error('Error updating subIndustry:', error);
            alert('Failed to update sub-industry');
        } finally {
            setSavingSubIndustry(false);
        }
    };

    const handleToggleMandatoryChannel = async (channel: string) => {
        if (!offer) return;
        const current = offer.mandatoryChannels || [];
        const next = current.includes(channel)
            ? current.filter((c) => c !== channel)
            : [...current, channel];
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { mandatoryChannels: next },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOffer({ ...offer, mandatoryChannels: next });
        } catch (error) {
            console.error('Error updating mandatoryChannels:', error);
            alert('Failed to update');
        }
    };

    const handleSavePrices = async () => {
        if (!offer) return;
        const sp = parseFloat(editSellerPrice);
        const bp = parseFloat(editBuyerPrice);
        if (isNaN(sp) || sp < 0 || isNaN(bp) || bp < 0) {
            setPriceSaveError('Prix invalides.');
            return;
        }
        setPriceSaveError(null);
        setSavingPrices(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { sellerPrice: sp, buyerPrice: bp },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOffer({ ...offer, sellerPrice: sp, buyerPrice: bp });
        } catch (error) {
            console.error('Error saving prices:', error);
            setPriceSaveError('Erreur lors de la sauvegarde.');
        } finally {
            setSavingPrices(false);
        }
    };

    const handleDeleteOffer = async () => {
        const confirmed = window.confirm(`Delete this offer permanently? This cannot be undone.`);
        if (!confirmed) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            navigate('/back-office/offers');
        } catch (error) {
            console.error('Error deleting offer:', error);
            alert('Failed to delete offer');
        }
    };

    const OFFER_STATUSES = ['new', 'in-progress', 'on_market', 'waiting_subscription', 'waiting_payment', 'running', 'locked', 'done', 'closed'];

    const handleStatusChange = async (newStatus: string) => {
        if (!offer || newStatus === offer.status) return;
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOffer({ ...offer, status: newStatus });
        } catch (error) {
            console.error('Error updating offer status:', error);
            alert('Failed to update offer status');
        }
    };

    const handleAdminReject = (leadId: string) => {
        setRejectReason('');
        setRejectingLeadId(leadId);
    };

    const closeRejectModal = () => {
        setRejectingLeadId(null);
        setRejectReason('');
    };

    const submitRejectModal = async () => {
        if (!rejectingLeadId || !rejectReason) return;

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${CONFIG.apiUrl}/lead/${rejectingLeadId}/admin-reject`,
                { rejectionReason: rejectReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            closeRejectModal();
            fetchOfferDetail();
        } catch (error) {
            console.error('Error rejecting lead:', error);
            alert('Failed to reject lead');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'validated':
            case 'paid':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
            case 'waiting':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'rejected':
            case 'refused':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getApprovalStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'conflict':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 font-bold';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getOfferStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'in-progress':
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'new':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'closed':
            case 'done':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            default:
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
    };

    if (loading && !offer) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-4 text-center">Loading...</div>
                </Card>
            </div>
        );
    }

    if (!offer) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-4 text-center">Offer not found</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            {/* Back button + Header */}
            <Card extra="p-5 mb-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/back-office/offers')}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        >
                            <MdArrowBack /> Back
                        </button>
                        <div>
                            <div className="text-2xl font-bold text-navy-700 dark:text-white">
                                Offer {offer.offerNumber || offer._id.slice(-8)}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                    offer.offerType === 'marketplace'
                                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                        : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                }`}>
                                    {offer.offerType === 'marketplace' ? 'Marketplace' : 'Direct'}
                                </span>
                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getOfferStatusColor(offer.status)}`}>
                                    {offer.status || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={offer.status || ''}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        >
                            {OFFER_STATUSES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                            {offer.status && !OFFER_STATUSES.includes(offer.status) && (
                                <option value={offer.status}>{offer.status}</option>
                            )}
                        </select>
                        <button
                            onClick={handleDeleteOffer}
                            className="rounded-lg px-4 py-2 text-sm font-semibold text-white bg-gray-400 hover:bg-gray-600 transition-colors"
                        >
                            Delete
                        </button>
                    </div>
                </div>

                {/* Offer info grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Industry</div>
                        <div className="text-sm text-navy-700 dark:text-white">
                            {offer.industry || '-'}
                        </div>
                        <div className="mt-1">
                            {(() => {
                                const currentIndustry = industries.find((i) => i.name === offer.industry);
                                const subIndustries = currentIndustry?.subIndustries || [];
                                if (!offer.industry || subIndustries.length === 0) {
                                    return offer.subIndustry ? (
                                        <span className="text-sm text-gray-500">/ {offer.subIndustry}</span>
                                    ) : null;
                                }
                                return (
                                    <select
                                        value={offer.subIndustry || ''}
                                        disabled={savingSubIndustry}
                                        onChange={(e) => handleSubIndustryChange(e.target.value)}
                                        className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                    >
                                        {!offer.subIndustry && <option value="">-</option>}
                                        {subIndustries.map((s) => (
                                            <option key={s.name} value={s.name}>{s.name}</option>
                                        ))}
                                    </select>
                                );
                            })()}
                        </div>
                    </div>
                    {offer.targetPrice && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Fourchette cible</div>
                            <div className="text-sm text-navy-700 dark:text-white">{offer.targetPrice}€</div>
                        </div>
                    )}
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Leads Generated</div>
                        <div className="text-sm text-navy-700 dark:text-white">{offer.leadsGenerated ?? 0}</div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Created</div>
                        <div className="text-sm text-navy-700 dark:text-white">
                            {new Date(offer.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Accept Side Seller</div>
                        <button
                            onClick={handleToggleAcceptSideSeller}
                            className={`mt-1 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                offer.acceptSideSeller ? 'bg-blue-600' : 'bg-gray-300 dark:bg-navy-600'
                            }`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                offer.acceptSideSeller ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                        </button>
                    </div>
                    {offer.channels && offer.channels.length > 0 && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Canaux</div>
                            <div className="text-sm text-navy-700 dark:text-white">{offer.channels.join(', ')}</div>
                        </div>
                    )}
                    <div className="md:col-span-2 lg:col-span-4">
                        <div className="mb-1 text-xs font-bold uppercase text-gray-500">Canaux obligatoires</div>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                            Si défini, seuls les vendeurs disposant d'un canal d'acquisition correspondant pourront recevoir des leads pour cette offre.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {allChannels.map((channel) => {
                                const isSelected = (offer.mandatoryChannels || []).includes(channel);
                                return (
                                    <button
                                        key={channel}
                                        type="button"
                                        onClick={() => handleToggleMandatoryChannel(channel)}
                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                            isSelected
                                                ? 'bg-brand-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                        }`}
                                    >
                                        {channel}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {offer.regions && offer.regions.length > 0 && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Regions</div>
                            <div className="text-sm text-navy-700 dark:text-white">{offer.regions.join(', ')}</div>
                        </div>
                    )}
                    {offer.sheetUrl && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Google Sheet</div>
                            <a
                                href={offer.sheetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                            >
                                Ouvrir le sheet
                            </a>
                        </div>
                    )}
                </div>
            </Card>

            {/* Pricing & Margin card */}
            {(() => {
                const sp = parseFloat(editSellerPrice);
                const bp = parseFloat(editBuyerPrice);
                const hasValidPrices = !isNaN(sp) && !isNaN(bp) && bp > 0;
                const grossMargin = hasValidPrices ? bp - sp : null;
                const grossMarginPct = hasValidPrices && bp > 0 ? ((bp - sp) / bp) * 100 : null;
                const sellerCost = sellerReferalInfo?.costPerLead ?? 0;
                const buyerCost = buyerReferalInfo?.costPerLead ?? 0;
                const totalCost = sellerCost + buyerCost;
                const netMargin = hasValidPrices ? bp - sp - totalCost : null;
                const netMarginPct = hasValidPrices && bp > 0 ? ((bp - sp - totalCost) / bp) * 100 : null;
                const hasAffiliateOrCoupon = totalCost > 0;

                const isEditable = offer.status === 'waiting_payment';
                const pricesChanged = isEditable && (
                    sp !== (offer.sellerPrice ?? NaN) ||
                    bp !== (offer.buyerPrice ?? NaN)
                );

                const fmtEur = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                const fmtPct = (n: number) => n.toFixed(1) + ' %';

                const typeLabel = (info: ReferalInfo) => {
                    if (info.type === 'affiliate+coupon') return `affilié + coupon`;
                    if (info.type === 'affiliate') return `affilié`;
                    if (info.type === 'coupon') return `coupon`;
                    return `référral`;
                };

                const marginColor = (v: number) => v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500';

                return (
                    <Card extra="p-4 mb-4">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                            {/* Prices */}
                            <div className="flex items-center gap-4">
                                <div>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Vendeur</p>
                                    {isEditable ? (
                                        <div className="relative w-28">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={editSellerPrice}
                                                onChange={(e) => setEditSellerPrice(e.target.value)}
                                                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-2.5 pr-6 text-sm font-semibold text-purple-600 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-purple-400"
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                            {offer.sellerPrice != null ? fmtEur(offer.sellerPrice) : '—'}
                                        </p>
                                    )}
                                </div>

                                <div className="text-gray-300 dark:text-navy-500 text-lg select-none">→</div>

                                <div>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Acheteur</p>
                                    {isEditable ? (
                                        <div className="relative w-28">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={editBuyerPrice}
                                                onChange={(e) => setEditBuyerPrice(e.target.value)}
                                                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-2.5 pr-6 text-sm font-semibold text-blue-600 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-blue-400"
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                            {offer.buyerPrice != null ? fmtEur(offer.buyerPrice) : '—'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            {hasValidPrices && <div className="hidden sm:block h-10 w-px bg-gray-200 dark:bg-navy-600" />}

                            {/* Margin stats */}
                            {hasValidPrices && (
                                <div className="flex flex-wrap items-start gap-4">
                                    {/* Gross */}
                                    <div>
                                        <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Marge brute</p>
                                        <p className={`text-sm font-bold ${marginColor(grossMargin!)}`}>
                                            {fmtEur(grossMargin!)}
                                            <span className="ml-1.5 text-xs font-semibold opacity-80">{fmtPct(grossMarginPct!)}</span>
                                        </p>
                                    </div>

                                    {/* Affiliate/coupon notes + net margin */}
                                    {hasAffiliateOrCoupon && (
                                        <>
                                            <div className="flex flex-col gap-1">
                                                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Déductions/lead</p>
                                                {sellerReferalInfo && sellerReferalInfo.costPerLead > 0 && (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800 px-2 py-0.5 text-[11px]">
                                                        <span className="font-bold text-orange-600 dark:text-orange-400">V</span>
                                                        <span className="font-mono text-orange-700 dark:text-orange-300">{sellerReferalInfo.code}</span>
                                                        <span className="text-gray-500">·</span>
                                                        <span className="text-gray-500 dark:text-gray-400">{typeLabel(sellerReferalInfo)}</span>
                                                        <span className="font-semibold text-red-500">−{fmtEur(sellerReferalInfo.costPerLead)}</span>
                                                    </span>
                                                )}
                                                {buyerReferalInfo && buyerReferalInfo.costPerLead > 0 && (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 px-2 py-0.5 text-[11px]">
                                                        <span className="font-bold text-blue-600 dark:text-blue-400">A</span>
                                                        <span className="font-mono text-blue-700 dark:text-blue-300">{buyerReferalInfo.code}</span>
                                                        <span className="text-gray-500">·</span>
                                                        <span className="text-gray-500 dark:text-gray-400">{typeLabel(buyerReferalInfo)}</span>
                                                        <span className="font-semibold text-red-500">−{fmtEur(buyerReferalInfo.costPerLead)}</span>
                                                    </span>
                                                )}
                                            </div>

                                            <div>
                                                <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">Marge nette</p>
                                                <p className={`text-sm font-bold ${marginColor(netMargin!)}`}>
                                                    {fmtEur(netMargin!)}
                                                    <span className="ml-1.5 text-xs font-semibold opacity-80">{fmtPct(netMarginPct!)}</span>
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Save button */}
                            {isEditable && (pricesChanged || priceSaveError) && (
                                <div className="ml-auto flex items-center gap-2">
                                    {priceSaveError && <span className="text-xs text-red-500">{priceSaveError}</span>}
                                    <button
                                        onClick={handleSavePrices}
                                        disabled={savingPrices || !pricesChanged}
                                        className="flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                                    >
                                        <MdSave className="text-sm" />
                                        {savingPrices ? 'Sauvegarde...' : 'Sauvegarder'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </Card>
                );
            })()}

            {/* Seller & Buyer cards */}
            <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                {/* Seller */}
                <Card extra="p-5">
                    <div className="mb-2 text-xs font-bold uppercase text-gray-500">Seller</div>
                    {offer.sellerId ? (
                        <div>
                            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                {userDisplayName(offer.sellerId)}
                            </div>
                            <div className="text-xs text-gray-500">
                                S: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(offer.sellerId!.email)}`)} className="underline text-brand-400 hover:text-brand-500">{offer.sellerId.email}</button>
                            </div>
                            {offer.sellerId.company && (
                                <div className="text-sm text-gray-500">{offer.sellerId.company}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-500">No seller assigned</div>
                    )}
                </Card>

                {/* Buyer */}
                <Card extra="p-5">
                    <div className="mb-2 text-xs font-bold uppercase text-gray-500">Buyer</div>
                    {offer.buyerId ? (
                        <div>
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {userDisplayName(offer.buyerId)}
                            </div>
                            <div className="text-xs text-gray-500">
                                B: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(offer.buyerId!.email)}`)} className="underline text-brand-400 hover:text-brand-500">{offer.buyerId.email}</button>
                            </div>
                            {offer.buyerId.company && (
                                <div className="text-sm text-gray-500">{offer.buyerId.company}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-500">No buyer assigned</div>
                    )}
                </Card>
            </div>

            {/* Leads table */}
            <Card extra="p-5">
                <div className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
                    Leads ({totalLeads})
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Order #</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Email</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Seller</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Buyer</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Approval</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leadrs Rate</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Customer Rate</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Commission</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-4 text-center">Loading...</td>
                                </tr>
                            ) : leads.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-4 text-center">No leads for this offer</td>
                                </tr>
                            ) : (
                                leads.map((lead) => (
                                    <tr key={lead._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 text-sm font-mono">
                                            {lead.orderNumber || lead._id.slice(-8)}
                                        </td>
                                        <td className="py-3 text-sm">{lead.email || '-'}</td>
                                        <td className="py-3 text-sm">
                                            {lead.sellerId ? (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.sellerId!.email)}`)}
                                                    className="underline text-purple-600 hover:text-purple-800 dark:text-purple-400"
                                                >
                                                    {lead.sellerId.email}
                                                </button>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {lead.buyerId ? (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.buyerId!.email)}`)}
                                                    className="underline text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                                >
                                                    {lead.buyerId.email}
                                                </button>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(lead.status)}`}>
                                                {lead.status}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            {lead.approvalStatus ? (
                                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getApprovalStatusColor(lead.approvalStatus)}`}>
                                                    {lead.approvalStatus}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-sm">{lead.leadrsRate ? `${lead.leadrsRate}/10` : '-'}</td>
                                        <td className="py-3 text-sm">{lead.customerRate >= 0 ? `${lead.customerRate}/10` : '-'}</td>
                                        <td className="py-3 text-sm font-medium text-green-600 dark:text-green-400">
                                            {lead.buyerPrice && lead.leadPrice ? `${(lead.buyerPrice - lead.leadPrice).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {new Date(lead.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="py-3">
                                            {lead.status !== 'rejected' && lead.approvalStatus !== 'rejected' && (
                                                <button
                                                    onClick={() => handleAdminReject(lead._id)}
                                                    className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                    title="Admin reject this lead"
                                                >
                                                    Reject
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft /> Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Reject Reason Modal */}
            {rejectingLeadId && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeRejectModal} />
                    <div className="relative z-10 w-full max-w-md m-4 rounded-lg bg-white shadow-xl dark:bg-navy-800">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                            <h2 className="text-lg font-bold text-navy-700 dark:text-white">Rejeter le lead</h2>
                            <button onClick={closeRejectModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <MdClose size={22} />
                            </button>
                        </div>

                        <div className="px-6 py-4">
                            <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                Raison du rejet
                            </label>
                            <select
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            >
                                <option value="">Sélectionner une raison...</option>
                                {REJECTION_REASONS.map((reason) => (
                                    <option key={reason} value={reason}>{reason}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-navy-600">
                            <button
                                onClick={closeRejectModal}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={submitRejectModal}
                                disabled={!rejectReason}
                                className="rounded-lg bg-red-500 px-4 py-2 text-sm text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
